<template>
  <section class="ontos-section" :class="{'ontos-section--flush': flush}">
    <v-divider v-if="divider"/>
    <ontos-page-header v-if="header" :title="title" :subtitle="subtitle" :icon="icon"
                       :level="level" :dense="dense">
      <!-- A prop continua sendo passada ao lado do slot: é ela que vira o `title` nativo
           da linha, e o conteúdo de quem não passa o slot. -->
      <!-- O lugar do título, à esquerda, quando ele é um CONTROLE e não uma palavra. O
           PageHeader já o oferecia; faltava este repasse, e sem ele quem tentava usá-lo caía
           no `tabs`, que fica no fim da linha, depois das ações. -->
      <template v-if="$slots.title" #title>
        <slot name="title"/>
      </template>
      <template v-if="$slots.subtitle" #subtitle>
        <slot name="subtitle"/>
      </template>
      <!-- As abas ficam na linha do título, como na página da coleção: é o mesmo gesto de
           alternar duas visões do mesmo conteúdo, e pôr no mesmo lugar é o que faz as duas
           telas se lerem igual. -->
      <template v-if="$slots.tabs" #tabs>
        <slot name="tabs"/>
      </template>
      <template v-if="$slots.actions" #actions>
        <slot name="actions"/>
      </template>
      <template v-if="$slots.controls" #controls>
        <slot name="controls"/>
      </template>
      <template v-if="$slots.below" #below>
        <slot name="below"/>
      </template>
    </ontos-page-header>
    <slot/>
  </section>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'

/**
 * Uma seção de página: divisória, cabeçalho e conteúdo.
 *
 * Existe porque o ritmo vertical estava escrito à mão em cada seção, com classes
 * utilitárias diferentes para o mesmo caso — `mt-5` na divisória de um lado, `my-0`
 * na linha de um e gutter padrão na do outro —, e o resultado era duas seções iguais
 * espaçadas de formas diferentes na mesma tela.
 *
 * O espaçamento é `gap` de flex, e não margem dos filhos, pela mesma razão que levou o
 * PageHeader a fazer isso: margem vertical de filho colapsa, some quando o filho é
 * condicional, e obriga cada tela a saber quanto vale o vão. Com gap, quem não é
 * renderizado não deixa buraco.
 */
@Component({
  name: 'ontos-section'
})
class Section extends Vue {
  @Prop({type: String, default: ''}) readonly title!: string
  /**
   * Linha menor sob o título, repassada ao PageHeader. Serve ao caso em que o título
   * não diz o tipo do conteúdo — a seção de um relacionamento na ficha da instância
   * se chama pelo relacionamento, e é o subtítulo que diz de que coleção são os itens.
   */
  @Prop({type: String, default: ''}) readonly subtitle!: string
  @Prop({type: String, default: ''}) readonly icon!: string
  /** Só a tag, para semântica: as seções de uma página são h2 */
  @Prop({type: [String, Number], default: 2}) readonly level!: string | number
  /**
   * Tipografia menor no título, repassada ao PageHeader. Para seção DENTRO de um
   * bloco que já tem cabeçalho próprio — uma parte de formulário, por exemplo —, onde
   * o tamanho de seção de página competiria com o título da tela.
   */
  @Prop({type: Boolean, default: false}) readonly dense!: boolean
  /** A primeira seção da página não precisa de régua acima */
  @Prop({type: Boolean, default: true}) readonly divider!: boolean
  /**
   * A régua vai de borda a borda da superfície, furando o respiro de quem hospeda — é a
   * divisória de um CARD, e não uma linha dentro do conteúdo.
   *
   * Quanto furar não se decide aqui: quem hospeda publica o próprio respiro em
   * `--ontos-section-bleed`, porque é ele quem sabe de quanto é. Sem a variável nada acontece,
   * e por isso ligar o modo numa tela sem card é inofensivo.
   */
  @Prop({type: Boolean, default: false}) readonly flush!: boolean
  /**
   * Seção única na tela não precisa de cabeçalho: não há de que se distinguir, e o
   * cabeçalho da página já nomeia o conteúdo. Fica o conteúdo, com o espaçamento.
   */
  @Prop({type: Boolean, default: true}) readonly header!: boolean
}

export default toNative(Section)
</script>
<style scoped>
/* A régua fica colada no cabeçalho de propósito: ela abre a seção, não separa duas
   coisas de peso igual. O vão maior é o de cima, entre esta seção e a anterior. */
.ontos-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;
}

.ontos-section:first-child {
  margin-top: 0;
}

/* De borda a borda: a régua sai do respiro do hospedeiro pelos dois lados. Margem negativa, e
   não largura — largura maior que o pai transbordaria para a direita e deixaria a esquerda
   dentro do respiro, torta. Só a RÉGUA fura; o conteúdo da seção continua respeitando o
   respiro, que é o que faz a linha parecer divisória de card. */
.ontos-section--flush > .v-divider {
  margin-left: calc(var(--ontos-section-bleed, 0px) * -1);
  margin-right: calc(var(--ontos-section-bleed, 0px) * -1);
}
</style>
